import {
  GIT_PROVIDERS,
  PROVIDER_NAMES,
  AUTH_FLOWS,
  AUTH_LEVEL
} from 'common/constants/constants';
import { API } from 'common/constants/api-urls';
import gitlabLogo from 'images/logos/gitlab-logo.svg';
import bitbucketLogo from 'images/logos/bitbucket-logo.svg';
import githubLogo from 'images/logos/github-logo.svg';
import azureLogo from 'images/logos/azure-devops.svg';
import replace from 'lodash/replace';
import { getOrganizationFromBaseUrl } from './utils';
import Text, { Inline } from 'components/base/Text';
import Link from '@material-ui/core/Link';
import noop from 'lodash/noop';
import workerbGithubLogo from 'images/logos/workerb-github-logo.svg';
import workerbGitlabLogo from 'images/logos/workerb-gitlab-logo.svg';
import workerbBitbucketLogo from 'images/logos/workerb-bitbucket-logo.svg';

const formatHttpUrl =
  (separator = '.') =>
  url => {
    const { pathname } = new URL(url);
    return {
      displayPath: pathname.split(separator)[0].replace('/', '')
    };
  };
const formatHttpUrlAzure = url => {
  const { pathname } = new URL(url);
  const organization = new URL(url).pathname.replace('/', '').split('/')[0];
  return {
    displayPath: decodeURI(
      replace(replace(pathname, '/_git', ''), `/${organization}`, '')
    ).replace('/', '')
  };
};
export const getProviderConfig = ({ provider, isCloudProvider }) => {
  const providersConfig = {
    [GIT_PROVIDERS.GITLAB]: {
      name: isCloudProvider ? 'GitLab' : 'GitLab Server',
      logo: gitlabLogo,
      showOrgNameField: false,
      showOauthSection: true,
      infoLink: isCloudProvider
        ? 'https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html'
        : 'https://linearb.helpdocs.io/article/h2a8lvx4l3-git-lab-server-on-prem-connection-guide',
      exampleUrl: 'https://gitlab.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/${PROVIDER_NAMES.gitlab}`,
      apiTestUrl: `${API.TEST_API_TOKEN_PATH}/${PROVIDER_NAMES.gitlab}`,
      formatHttpUrl: formatHttpUrl('.git'),
      helpDocForPAT:
        'https://linearb.helpdocs.io/article/mvx1erzgqi-create-a-personal-access-token-in-git-lab'
    },
    [GIT_PROVIDERS.BITBUCKET_SERVER]: {
      name: 'Bitbucket Server',
      logo: bitbucketLogo,
      showOrgNameField: false,
      showOauthSection: true,
      infoLink:
        'https://confluence.atlassian.com/bitbucketserver/personal-access-tokens-939515499.html',
      exampleUrl: 'https://bitbucket.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/${PROVIDER_NAMES.bitbucket_server}`,
      apiTestUrl: `${API.TEST_API_TOKEN_PATH}/${PROVIDER_NAMES.bitbucket_server}`,
      formatHttpUrl: formatHttpUrl()
    },
    [GIT_PROVIDERS.BITBUCKET]: {
      name: 'Bitbucket',
      logo: bitbucketLogo,
      showOrgNameField: false,
      showOauthSection: true,
      infoLink:
        'https://confluence.atlassian.com/bitbucketserver/personal-access-tokens-939515499.html',
      formatHttpUrl: formatHttpUrl(),
      apiTestUrl: `${API.TEST_API_TOKEN_PATH}/${PROVIDER_NAMES.bitbucket}`
    },
    [GIT_PROVIDERS.GITHUB]: {
      name: isCloudProvider ? 'GitHub' : 'GitHub Enterprise Server',
      logo: githubLogo,
      showOrgNameField: false,
      showOauthSection: true,
      infoLink:
        'https://linearb.helpdocs.io/article/qlzg38hn0b-connect-github-with-a-personal-access-token',
      exampleUrl: 'https://github.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/${PROVIDER_NAMES.github}`,
      apiTestUrl: `${API.TEST_API_TOKEN_PATH}/${PROVIDER_NAMES.github}`,
      formatHttpUrl: formatHttpUrl(),
      helpDocForPAT:
        'https://linearb.helpdocs.io/article/qlzg38hn0b-connect-github-with-a-personal-access-token'
    },
    [GIT_PROVIDERS.AZURE_DEVOPS]: {
      name: 'Azure DevOps',
      logo: azureLogo,
      showOrgNameField: true,
      showOauthSection: false,
      baseUrl: 'https://dev.azure.com/',
      infoLink:
        'https://linearb.helpdocs.io/article/3jkmufmduv-connect-azure-dev-ops-using-a-personal-access-token',
      exampleUrl: 'https://azure.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}${PROVIDER_NAMES.azure_devops}`,
      apiTestUrl: `${API.TEST_API_TOKEN_PATH}/${PROVIDER_NAMES.azure_devops}`,
      baseUrlForTest: (baseUrl, params) => `${baseUrl}${params}`,
      formatHttpUrl: formatHttpUrlAzure,
      organizationFromBaseUrl: getOrganizationFromBaseUrl,
      helpDocForPAT:
        'https://linearb.helpdocs.io/article/3jkmufmduv-connect-azure-dev-ops-using-a-personal-access-token'
    }
  };
  const providerTypeId = GIT_PROVIDERS[provider?.toUpperCase()];
  return providerTypeId ? providersConfig[providerTypeId] : {};
};
export const validationStatusToMessageMap = ({
  isPat = false,
  onInviteAdmin = noop,
  missingScopes = []
} = {}) => ({
  VALID: 'VALID',
  INVALID: (
    <Inline size='noraml'>
      {isPat
        ? 'Seems like the token lacks proper permissions. Time to invite an admin to the rescue!'
        : "Oops! Looks like there's an authentication hiccup. Double-check you allowed the right scopes in your Git provider permissions and try again."}{' '}
      <Link onClick={onInviteAdmin}>
        {isPat ? 'Invite your admin' : 'Not the admin?'}
      </Link>
    </Inline>
  ),
  ERROR: (
    <Inline size='noraml'>
      Oh no! The token can't be validated by the Git provider.{' '}
      <Link onClick={onInviteAdmin}>Not the admin?</Link>
    </Inline>
  ),
  MISSING_SCOPES: (
    <Text size='noraml'>
      It seems the token is lacking certain permission scopes. Be sure to select
      all the missing scopes: [ {missingScopes.join(', ')} ]
    </Text>
  ),
  EXPIRED: (
    <Text size='noraml'>
      Uh-oh! The access token has expired. Time for a fresh one!
    </Text>
  ),
  NOT_RELATED_TO_ORG: (
    <Inline size='noraml'>
      No organization found on your GitHub account. LinearB needs repos within
      an org. <Link onClick={onInviteAdmin}>Not the admin?</Link>
    </Inline>
  )
});

export const getProviderDetailsLinkAccount = (
  authFlow,
  authLevel,
  providerName,
  isOnPrem
) => {
  const isGithubPersonalOauthFromSlack =
    authFlow === AUTH_FLOWS.LINK_ACCOUNT && authLevel === AUTH_LEVEL.INDIVIDUAL;

  if (!isGithubPersonalOauthFromSlack && !isOnPrem) {
    return {};
  }
  const logos = {
    github: workerbGithubLogo,
    gitlab: workerbGitlabLogo,
    bitbucket: workerbBitbucketLogo,
    bitbucket_server: workerbBitbucketLogo
  };
  const providerNameLowerCase = providerName.toLowerCase();
  const isCloudProvider = !isOnPrem;
  const providerConfig = getProviderConfig({
    provider: providerNameLowerCase,
    isCloudProvider
  });
  const workerbGitProviderLogo = logos[providerNameLowerCase];
  return { ...providerConfig, workerGitLogo: workerbGitProviderLogo };
};
